import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { requestWithAuthorization } from '../network/apiRequests';
import { GPSCoorindate } from '../models/GPSCoorindate';

export interface SessionData {
  name: string;
  startDateTime: string;
  endDateTime: string;
  infos: any;
}

interface SessionCache {
  details: SessionData | null;
  coordinates: GPSCoorindate[];
  plotData: any[];
  plotDataRanges: Record<string, { data: any[]; lastFetched: number }>; // Cache data by time range
  hasThirdPartyData: boolean | null;
  lastFetched: number;
}

interface SessionsState {
  sessions: Record<string, SessionCache>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
}

const initialState: SessionsState = {
  sessions: {},
  loading: {},
  errors: {},
};

// Cache duration in milliseconds (1 hour)
const CACHE_DURATION = 60 * 60 * 1000;

// Async thunks for API calls
export const fetchThirdPartyStatus = createAsyncThunk(
  'sessions/fetchThirdPartyStatus',
  async (sessionId: string) => {
    try {
      const response = await requestWithAuthorization(
        `${process.env.REACT_APP_API_HOSTNAME}/recordings/third-party-exists?recordingsId=${sessionId}`
      );
      if (response.status === 200) {
        const exists = response.data?.exists ?? response.data?.thirdPartyExists ?? response.data === true;
        return Boolean(exists);
      }
      return false;
    } catch (err) {
      console.error("Failed to check third-party data status:", err);
      return false;
    }
  }
);

export const fetchSessionDetails = createAsyncThunk(
  'sessions/fetchDetails',
  async (sessionId: string) => {
    const response = await requestWithAuthorization(
      process.env.REACT_APP_API_HOSTNAME + "/recordings/" + sessionId + "/details"
    );
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch session details');
    }
  }
);

export const fetchSessionCoordinates = createAsyncThunk(
  'sessions/fetchCoordinates',
  async (sessionId: string) => {
    const response = await requestWithAuthorization(
      process.env.REACT_APP_API_HOSTNAME + "/recordings/" + sessionId + "/gps"
    );
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch session coordinates');
    }
  }
);

export const fetchSessionPlotData = createAsyncThunk(
  'sessions/fetchPlotData',
  async ({ sessionId, sensorId }: { sessionId: string; sensorId: string }) => {
    const response = await requestWithAuthorization(
      process.env.REACT_APP_API_HOSTNAME + "/recordings/plotdata?fileId=" + sessionId + "&sensorId=" + sensorId
    );
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch session plot data');
    }
  }
);

// Thunk to fetch plot data for a specific time range
export const fetchSessionPlotDataRange = createAsyncThunk(
  'sessions/fetchPlotDataRange',
  async ({ sessionId, sensorId, start, end }: { sessionId: string; sensorId: string; start?: string; end?: string }) => {
    let url = process.env.REACT_APP_API_HOSTNAME + "/recordings/plotdata?fileId=" + sessionId + "&sensorId=" + sensorId;
    
    if (start) {
      url += `&start=${start}`;
      if (end) {
        url += `&end=${end}`;
      }
    } else if (end) {
      url += `&end=${end}`;
    }
    
    const response = await requestWithAuthorization(url);
    
    if (response.status === 200) {
      return { sessionId, start, end, data: response.data };
    } else {
      throw new Error('Failed to fetch plot data range');
    }
  }
);

// Combined thunk to fetch all session data
export const fetchCompleteSessionData = createAsyncThunk(
  'sessions/fetchComplete',
  async ({ sessionId, sensorId }: { sessionId: string; sensorId: string }, { dispatch }) => {
    const [details, coordinates, plotData, hasThirdPartyData] = await Promise.all([
      dispatch(fetchSessionDetails(sessionId)).unwrap(),
      dispatch(fetchSessionCoordinates(sessionId)).unwrap(),
      dispatch(fetchSessionPlotData({ sessionId, sensorId })).unwrap(),
      dispatch(fetchThirdPartyStatus(sessionId)).unwrap(),
    ]);
    
    return { sessionId, details, coordinates, plotData, hasThirdPartyData };
  }
);

const sessionsSlice = createSlice({
  name: 'sessions',
  initialState,
  reducers: {
    clearSessionCache: (state, action: PayloadAction<string>) => {
      delete state.sessions[action.payload];
      delete state.loading[action.payload];
      delete state.errors[action.payload];
    },
    clearAllCache: (state) => {
      state.sessions = {};
      state.loading = {};
      state.errors = {};
    },
    setThirdPartyStatus: (state, action: PayloadAction<{ sessionId: string; hasThirdPartyData: boolean }>) => {
      const { sessionId, hasThirdPartyData } = action.payload;
      if (state.sessions[sessionId]) {
        state.sessions[sessionId].hasThirdPartyData = hasThirdPartyData;
      }
    },
  },
  extraReducers: (builder) => {
    // Third party status
    builder
      .addCase(fetchThirdPartyStatus.fulfilled, (state, action) => {
        const sessionId = action.meta.arg;
        if (!state.sessions[sessionId]) {
          state.sessions[sessionId] = {
            details: null,
            coordinates: [],
            plotData: [],
            plotDataRanges: {},
            hasThirdPartyData: null,
            lastFetched: 0,
          };
        }
        state.sessions[sessionId].hasThirdPartyData = action.payload;
      });

    // Session details
    builder
      .addCase(fetchSessionDetails.pending, (state, action) => {
        state.loading[action.meta.arg] = true;
        state.errors[action.meta.arg] = null;
      })
      .addCase(fetchSessionDetails.fulfilled, (state, action) => {
        const sessionId = action.meta.arg;
        if (!state.sessions[sessionId]) {
          state.sessions[sessionId] = {
            details: null,
            coordinates: [],
            plotData: [],
            plotDataRanges: {},
            hasThirdPartyData: null,
            lastFetched: 0,
          };
        }
        state.sessions[sessionId].details = action.payload;
        state.sessions[sessionId].lastFetched = Date.now();
        state.loading[sessionId] = false;
      })
      .addCase(fetchSessionDetails.rejected, (state, action) => {
        state.loading[action.meta.arg] = false;
        state.errors[action.meta.arg] = action.error.message || 'Failed to fetch session details';
      });

    // Session coordinates
    builder
      .addCase(fetchSessionCoordinates.fulfilled, (state, action) => {
        const sessionId = action.meta.arg;
        if (!state.sessions[sessionId]) {
          state.sessions[sessionId] = {
            details: null,
            coordinates: [],
            plotData: [],
            plotDataRanges: {},
            hasThirdPartyData: null,
            lastFetched: 0,
          };
        }
        state.sessions[sessionId].coordinates = action.payload;
        state.sessions[sessionId].lastFetched = Date.now();
      });

    // Session plot data
    builder
      .addCase(fetchSessionPlotData.pending, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        state.loading[sessionId] = true;
        state.errors[sessionId] = null;
      })
      .addCase(fetchSessionPlotData.fulfilled, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        if (!state.sessions[sessionId]) {
          state.sessions[sessionId] = {
            details: null,
            coordinates: [],
            plotData: [],
            plotDataRanges: {},
            hasThirdPartyData: null,
            lastFetched: 0,
          };
        }
        state.sessions[sessionId].plotData = action.payload;
        state.sessions[sessionId].lastFetched = Date.now();
        state.loading[sessionId] = false;
      })
      .addCase(fetchSessionPlotData.rejected, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        state.loading[sessionId] = false;
        state.errors[sessionId] = action.error.message || 'Failed to fetch plot data';
      });


    // Complete session data
    builder
      .addCase(fetchCompleteSessionData.pending, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        state.loading[sessionId] = true;
        state.errors[sessionId] = null;
      })
      .addCase(fetchCompleteSessionData.fulfilled, (state, action) => {
        const { sessionId, details, coordinates, plotData, hasThirdPartyData } = action.payload;
        state.sessions[sessionId] = {
          details,
          coordinates,
          plotData,
          plotDataRanges: {},
          hasThirdPartyData,
          lastFetched: Date.now(),
        };
        state.loading[sessionId] = false;
      })
      .addCase(fetchCompleteSessionData.rejected, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        state.loading[sessionId] = false;
        state.errors[sessionId] = action.error.message || 'Failed to fetch session data';
      });

    // Plot data range
    builder
      .addCase(fetchSessionPlotDataRange.fulfilled, (state, action) => {
        const { sessionId, start, end, data } = action.payload;
        const rangeKey = `${start || 'start'}-${end || 'end'}`;
        
        if (!state.sessions[sessionId]) {
          state.sessions[sessionId] = {
            details: null,
            coordinates: [],
            plotData: [],
            plotDataRanges: {},
            hasThirdPartyData: null,
            lastFetched: 0,
          };
        }
        
        // Cache the range-specific data
        state.sessions[sessionId].plotDataRanges[rangeKey] = {
          data,
          lastFetched: Date.now(),
        };
      });
  },
});

// Selectors
export const selectSessionData = (state: any, sessionId: string) => 
  state.sessions.sessions[sessionId];

export const selectSessionDetails = (state: any, sessionId: string) => 
  state.sessions.sessions[sessionId]?.details;

export const selectSessionCoordinates = (state: any, sessionId: string) => 
  state.sessions.sessions[sessionId]?.coordinates || [];

export const selectSessionPlotData = (state: any, sessionId: string) => 
  state.sessions.sessions[sessionId]?.plotData || [];

export const selectSessionPlotDataRange = (state: any, sessionId: string, start?: string, end?: string) => {
  const rangeKey = `${start || 'start'}-${end || 'end'}`;
  return state.sessions.sessions[sessionId]?.plotDataRanges[rangeKey]?.data || [];
};

export const selectIsPlotDataRangeCached = (state: any, sessionId: string, start?: string, end?: string) => {
  const rangeKey = `${start || 'start'}-${end || 'end'}`;
  const range = state.sessions.sessions[sessionId]?.plotDataRanges[rangeKey];
  if (!range || !range.lastFetched) return false;
  
  const isExpired = Date.now() - range.lastFetched > CACHE_DURATION;
  return !isExpired;
};

export const selectIsSessionLoading = (state: any, sessionId: string) => 
  state.sessions.loading[sessionId] || false;

export const selectSessionError = (state: any, sessionId: string) => 
  state.sessions.errors[sessionId];

// Helper selector to check if data is cached and fresh
export const selectIsSessionCached = (state: any, sessionId: string) => {
  const session = state.sessions.sessions[sessionId];
  if (!session || !session.lastFetched) return false;
  
  const isExpired = Date.now() - session.lastFetched > CACHE_DURATION;
  return !isExpired && session.details !== null;
};

export const selectHasThirdPartyData = (state: any, sessionId: string) =>
  state.sessions.sessions[sessionId]?.hasThirdPartyData ?? null;

export const { clearSessionCache, clearAllCache, setThirdPartyStatus } = sessionsSlice.actions;
export default sessionsSlice.reducer;